import { useState } from "react";
import { Link } from "react-router-dom";
import { Check, Minus, ArrowRight } from "lucide-react";
import { PLANS, planSignupPath } from "@/lib/plans";
import { sb } from "@/lib/text";

const euro = (n) => "€" + n.toFixed(2).replace(".", ",").replace(",00", "");

function BillingToggle({ billing, setBilling }) {
  return (
    <div className="inline-flex items-center bg-slate-100 rounded-full p-1" role="tablist" aria-label="Periodo di fatturazione">
      <button role="tab" aria-selected={billing === "monthly"} data-testid="billing-monthly" onClick={() => setBilling("monthly")}
        className={`rounded-full px-6 py-2.5 text-sm font-semibold transition-colors ${billing === "monthly" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}>Mensile</button>
      <button role="tab" aria-selected={billing === "annual"} data-testid="billing-annual" onClick={() => setBilling("annual")}
        className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-colors inline-flex items-center gap-2 ${billing === "annual" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}>
        Annuale <span className="text-[10px] font-bold bg-blue-100 text-blue-700 rounded-full px-2 py-0.5">2 mesi gratis</span>
      </button>
    </div>
  );
}

function selectableProps(selected, onSelect) {
  return {
    role: "button", tabIndex: 0, "aria-pressed": selected, onClick: onSelect,
    onKeyDown: (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onSelect(); } },
  };
}

function FullCard({ plan, billing, selected, onSelect }) {
  return (
    <div {...selectableProps(selected, onSelect)} data-testid={`plan-card-${plan.id}`}
      className={`relative rounded-3xl border-2 p-6 sm:p-7 bg-white cursor-pointer outline-none transition-all duration-200 flex flex-col ${
        selected ? "border-blue-500 bg-blue-50/40 shadow-xl shadow-blue-500/10 -translate-y-0.5"
          : "border-slate-200 hover:border-blue-300 hover:bg-blue-50/20 hover:-translate-y-0.5 hover:shadow-lg focus-visible:border-blue-400"}`}>
      {plan.badge && <span className="absolute -top-3 left-6 bg-blue-500 text-white text-xs font-bold rounded-full px-3 py-1 shadow-sm">{plan.badge}</span>}
      <p className="font-display font-bold text-sm text-slate-500 uppercase tracking-widest">{plan.name}</p>
      <p className="font-display font-bold text-xl text-slate-900 mt-2 leading-tight">{plan.headline}</p>
      <div className="mt-4 flex items-end gap-1.5">
        <span key={billing} className="font-display font-extrabold text-4xl text-slate-900 animate-price-pop">{plan.priceLabel[billing]}</span>
        <span className="text-slate-500 text-sm mb-1.5">{plan.isTrial ? plan.priceSuffix : `/ ${billing === "annual" ? "anno" : "mese"}`}</span>
      </div>
      {!plan.isTrial && plan.billingNote && <p key={`n-${billing}`} className="mt-2 text-xs text-slate-400 animate-price-pop">{plan.billingNote[billing]}</p>}
      <p className="text-sm text-slate-500 mt-3 leading-relaxed">{plan.description}</p>
      <Link to={`${planSignupPath(plan.id)}${plan.isTrial ? "" : `&billing=${billing}`}`} data-testid={`plan-cta-${plan.id}`} onClick={(e) => e.stopPropagation()}
        className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full py-3 font-semibold transition-all bg-blue-500 text-white shadow-lg shadow-blue-500/25 hover:bg-blue-600">
        {plan.cta} <ArrowRight className="w-4 h-4" />
      </Link>
      {plan.ctaNote && <p className="text-xs text-slate-400 mt-2 text-center">{plan.ctaNote}</p>}
      <ul className="mt-6 space-y-2.5 border-t border-slate-100 pt-5">
        {plan.features.map((f) => (
          <li key={f} className="flex gap-2.5 text-sm text-slate-700"><Check className="w-4 h-4 text-blue-500 shrink-0 mt-0.5" />{f}</li>
        ))}
        {(plan.limitations || []).map((l) => (
          <li key={l} className="flex gap-2.5 text-sm text-slate-400"><Minus className="w-4 h-4 shrink-0 mt-0.5" />{l}</li>
        ))}
      </ul>
    </div>
  );
}

export function PricingSection({ billing: extBilling, setBilling: extSet } = {}) {
  const [localBilling, setLocalBilling] = useState("monthly");
  const billing = extBilling ?? localBilling;
  const setBilling = extSet ?? setLocalBilling;
  const [selected, setSelected] = useState("pro");
  return (
    <section id="pricing" className="max-w-6xl mx-auto px-5 py-16" data-testid="pricing-section">
      <div className="text-center">
        <h2 className="font-display font-bold text-3xl tracking-tight">Scegli il piano giusto per la tua struttura</h2>
        <p className="mt-3 text-slate-500 max-w-xl mx-auto">{sb("Prova gratuitamente tutte le funzionalità Pro per 7 giorni. Poi scegli il piano più adatto a te.")}</p>
      </div>
      <div className="mt-8 flex justify-center"><BillingToggle billing={billing} setBilling={setBilling} /></div>
      <div className="mt-10 grid md:grid-cols-3 gap-5 items-start">
        {PLANS.map((p) => (
          <FullCard key={p.id} plan={p} billing={billing} selected={selected === p.id} onSelect={() => setSelected(p.id)} />
        ))}
      </div>
    </section>
  );
}

function CompactCard({ plan, billing, selected, onSelect }) {
  const tagline = plan.isTrial ? plan.compact.tagline : `${plan.priceLabel[billing]}/${billing === "annual" ? "anno" : "mese"}`;
  return (
    <div {...selectableProps(selected, onSelect)} data-testid={`plan-compact-${plan.id}`}
      className={`relative rounded-3xl border-2 p-6 bg-white cursor-pointer outline-none transition-all duration-200 flex flex-col ${
        selected ? "border-blue-500 bg-blue-50/40 shadow-xl shadow-blue-500/10 -translate-y-0.5"
          : "border-slate-200 hover:border-blue-300 hover:bg-blue-50/20 hover:-translate-y-0.5 hover:shadow-lg"}`}>
      {plan.badge && <span className="absolute -top-3 left-6 bg-blue-500 text-white text-xs font-bold rounded-full px-3 py-1 shadow-sm">{plan.badge}</span>}
      <p className="font-display font-bold text-sm text-slate-500 uppercase tracking-widest">{plan.name}</p>
      <p key={billing} className="font-display font-extrabold text-2xl text-slate-900 mt-1 animate-price-pop">{tagline}</p>
      <p className="text-xs text-slate-400 mt-2 leading-relaxed">{plan.compact.highlights}</p>
      <Link to={`${planSignupPath(plan.id)}${plan.isTrial ? "" : `&billing=${billing}`}`} data-testid={`plan-compact-cta-${plan.id}`} onClick={(e) => e.stopPropagation()}
        className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full py-2.5 font-semibold text-sm transition-all bg-blue-500 text-white shadow-lg shadow-blue-500/25 hover:bg-blue-600">
        {plan.ctaCompact} <ArrowRight className="w-4 h-4" />
      </Link>
    </div>
  );
}

export function PricingCompact({ billing: extBilling, setBilling: extSet } = {}) {
  const [localBilling, setLocalBilling] = useState("monthly");
  const billing = extBilling ?? localBilling;
  const setBilling = extSet ?? setLocalBilling;
  const [selected, setSelected] = useState("pro");
  return (
    <section className="max-w-5xl mx-auto px-5 py-16" data-testid="pricing-compact">
      <div className="text-center">
        <h2 className="font-display font-bold text-2xl sm:text-3xl tracking-tight">Pronto a migliorare l'esperienza dei tuoi ospiti?</h2>
        <p className="mt-3 text-slate-500 max-w-lg mx-auto">{sb("Prova tutto gratis per 7 giorni. Scegli il piano solo quando sei pronto.")}</p>
      </div>
      <div className="mt-8 flex justify-center"><BillingToggle billing={billing} setBilling={setBilling} /></div>
      <div className="mt-8 grid sm:grid-cols-3 gap-5">
        {PLANS.map((p) => (
          <CompactCard key={p.id} plan={p} billing={billing} selected={selected === p.id} onSelect={() => setSelected(p.id)} />
        ))}
      </div>
    </section>
  );
}
